import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  CButton, 
  CCard, 
  CCardBody, 
  CCol, 
  CContainer, 
  CForm, 
  CFormLabel, 
  CFormTextarea, 
  CRow, 
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CAlert
} from "@coreui/react";
import Navbar from "../../../components/NavBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { 
  getProfileChangeRequestByToken, 
  processProfileChangeRequest,
  checkAuthenticationStatus
} from "../../../../service-layer/ProfileChangeRequestService";
import "./ProfileChangeReview.css";
import AuthGuard from "../../../components/AuthGuard/AuthGuard";
import Sidebar from "../../../components/SideBar";
import { FaArrowLeft } from "react-icons/fa";

const ProfileChangeReview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Get token from URL parameters
  const query = new URLSearchParams(location.search);
  const token = query.get("token");

  // State management
  const [requestData, setRequestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState("");
  const [decision, setDecision] = useState(null);

  useEffect(() => {
    if (!token) {
      setError(t("invalid_or_missing_token"));
      setLoading(false);
      return;
    }

    // First check if user is admin before allowing access to the review page
    checkAdminAccess();
  }, [token]);

  const checkAdminAccess = async () => {
    try {
      setLoading(true);
      
      // First verify admin status
      const authResponse = await checkAuthenticationStatus();
      
      if (!authResponse || authResponse.status !== 200) {
        setError(t("authentication_required"));
        setLoading(false);
        return;
      }
      
      // Check if user is admin
      if (!authResponse.data.isAdmin) {
        setError(t("administrative_privileges_required"));
        setLoading(false);
        return;
      }
      
      // User is admin, now load the request data
      await loadRequestData();
      
    } catch (error) {
      console.error("Error checking admin access:", error);
      setError(t("access_denied"));
      setLoading(false);
    }
  };

  const loadRequestData = async () => {
    try {
      const response = await getProfileChangeRequestByToken(token);
      
      if (response && response.status === 200) {
        setRequestData(response.data);
      } else if (response && response.status === 404) {
        setError(t("request_not_found_or_expired"));
      } else if (response && response.status === 410) {
        setError(t("request_already_processed"));
      } else {
        setError(t("error_loading_request"));
      }
    } catch (error) {
      console.error("Error loading request data:", error);
      setError(t("error_loading_request"));
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async (approvalDecision) => {
    if (!requestData) return;

    try {
      setProcessing(true);
      setDecision(approvalDecision);

      const response = await processProfileChangeRequest(
        token, 
        approvalDecision, 
        message.trim()
      );

      if (response && response.status === 200) {
        toast.success(
          approvalDecision === 'APPROVED' 
            ? t("request_approved_successfully") 
            : t("request_rejected_successfully")
        );
        
        // Disable further actions
        setRequestData({
          ...requestData,
          status: approvalDecision === 'APPROVED' ? 'APPROVED' : 'REJECTED',
          processedAt: new Date().toISOString()
        });
      } else {
        toast.error(t("error_processing_request"));
        setDecision(null);
      }
    } catch (error) {
      console.error("Error processing request:", error);
      toast.error(t("error_processing_request"));
      setDecision(null);
    } finally {
      setProcessing(false);
    }
  };

  const handleBackToPendingRequests = () => {
    navigate('/pending_profile_changes');
  };

  const token_auth = window.localStorage.getItem("auth_token");

  // Show loading state
  if (loading) {
    return (
      <AuthGuard requireAdmin={true}>
        {token_auth && (
          <div className="dashboard d-flex">
            <div>
              <Sidebar />
            </div>
            <div
              style={{
                flex: "1 1 auto",
                display: "flex",
                flexFlow: "column",
                height: "100vh",
                overflowY: "hidden",
              }}
            >
              <Navbar />
              <div className="d-flex justify-content-center align-items-center" style={{ height: "100%" }}>
                <div className="text-center">
                  <CSpinner color="primary" size="lg" />
                  <p className="mt-3">{t("loading_request_details")}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </AuthGuard>
    );
  }

  // Show error state
  if (error) {
    return (
      <AuthGuard requireAdmin={true}>
        {token_auth && (
          <div className="dashboard d-flex">
            <div>
              <Sidebar />
            </div>
            <div
              style={{
                flex: "1 1 auto",
                display: "flex",
                flexFlow: "column",
                height: "100vh",
                overflowY: "auto",
              }}
            >
              <Navbar />
              <div style={{ padding: "20px" }}>
                <CContainer>
                  <CAlert color="danger">
                    <h4>{t("access_denied")}</h4>
                    <p>{error}</p>
                    <div className="d-flex gap-2">
                      <CButton 
                        color="secondary"
                        onClick={handleBackToPendingRequests}
                      >
                        <FaArrowLeft className="me-2" />
                        {t("go_back")}
                      </CButton>
                      <CButton 
                        color="primary"
                        onClick={() => navigate('/dashboard_admin')}
                      >
                        {t("go_to_dashboard")}
                      </CButton>
                    </div>
                  </CAlert>
                </CContainer>
              </div>
            </div>
          </div>
        )}
      </AuthGuard>
    );
  }

  // Show no data state
  if (!requestData) {
    return (
      <AuthGuard requireAdmin={true}>
        {token_auth && (
          <div className="dashboard d-flex">
            <div>
              <Sidebar />
            </div>
            <div
              style={{
                flex: "1 1 auto",
                display: "flex",
                flexFlow: "column",
                height: "100vh",
                overflowY: "auto",
              }}
            >
              <Navbar />
              <div style={{ padding: "20px" }}>
                <CContainer>
                  <CAlert color="warning">
                    <h4>{t("no_data")}</h4>
                    <p>{t("no_request_data_available")}</p>
                    <CButton 
                      color="secondary"
                      onClick={handleBackToPendingRequests}
                    >
                      <FaArrowLeft className="me-2" />
                      {t("go_back")}
                    </CButton>
                  </CAlert>
                </CContainer>
              </div>
            </div>
          </div>
        )}
      </AuthGuard>
    );
  }

  const isProcessed = requestData.status === 'APPROVED' || requestData.status === 'REJECTED';

  // Main content
  return (
    <AuthGuard requireAdmin={true}>
      {token_auth && (
        <div className="dashboard d-flex">
          <div>
            <Sidebar />
          </div>
          <div
            style={{
              flex: "1 1 auto",
              display: "flex",
              flexFlow: "column",
              height: "100vh",
              overflowY: "auto",
            }}
          >
            <Navbar />
            <div style={{ padding: "20px" }}>
              <ToastContainer position="top-center" />
              <CContainer fluid>
                {/* Header with Back Button */}
                <CRow className="mb-3">
                  <CCol md={12}>
                    <div className="d-flex align-items-center mb-3">
                      <CButton
                        color="secondary"
                        variant="outline"
                        onClick={handleBackToPendingRequests}
                        className="me-3"
                      >
                        <FaArrowLeft className="me-2" />
                        {t("go_back")}
                      </CButton>
                      <h2 className="mb-0">{t("profile_change_request_review")}</h2>
                    </div>
                  </CCol>
                </CRow>

                {isProcessed && (
                  <CRow className="mb-4">
                    <CCol md={12}>
                      <CAlert color={requestData.status === 'APPROVED' ? 'success' : 'danger'}>
                        <h5>
                          {requestData.status === 'APPROVED' 
                            ? t("request_already_approved") 
                            : t("request_already_rejected")
                          }
                        </h5>
                        <p>
                          {t("processed_on")}: {new Date(requestData.processedAt).toLocaleString()}
                        </p>
                      </CAlert>
                    </CCol>
                  </CRow>
                )}

                <CRow className="mb-4">
                  <CCol md={12}>
                    <CCard className="request-info-card">
                      <CCardBody>
                        <h3 className="mb-3">{t("doctor_information")}</h3>
                        <CTable borderless>
                          <CTableBody>
                            <CTableRow>
                              <CTableHeaderCell scope="row">{t("name")}:</CTableHeaderCell>
                              <CTableDataCell>{requestData.doctorName}</CTableDataCell>
                            </CTableRow>
                            <CTableRow>
                              <CTableHeaderCell scope="row">{t("surname")}:</CTableHeaderCell>
                              <CTableDataCell>{requestData.doctorSurname}</CTableDataCell>
                            </CTableRow>
                            <CTableRow>
                              <CTableHeaderCell scope="row">{t("fiscalCode")}:</CTableHeaderCell>
                              <CTableDataCell>{requestData.doctorFiscalCode}</CTableDataCell>
                            </CTableRow>
                            <CTableRow>
                              <CTableHeaderCell scope="row">{t("registrationNumber")}:</CTableHeaderCell>
                              <CTableDataCell>{requestData.doctorRegistrationNumber}</CTableDataCell>
                            </CTableRow>
                            <CTableRow>
                              <CTableHeaderCell scope="row">{t("request_date")}:</CTableHeaderCell>
                              <CTableDataCell>{new Date(requestData.requestDate).toLocaleString()}</CTableDataCell>
                            </CTableRow>
                          </CTableBody>
                        </CTable>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>

                <CRow className="mb-4">
                  <CCol md={12}>
                    <CCard className="changes-comparison-card">
                      <CCardBody>
                        <h3 className="mb-4">{t("requested_changes")}</h3>
                        <CTable striped bordered hover>
                          <CTableHead>
                            <CTableRow>
                              <CTableHeaderCell>{t("field")}</CTableHeaderCell>
                              <CTableHeaderCell>{t("current_value")}</CTableHeaderCell>
                              <CTableHeaderCell>{t("requested_value")}</CTableHeaderCell>
                              <CTableHeaderCell>{t("changed")}</CTableHeaderCell>
                            </CTableRow>
                          </CTableHead>
                          <CTableBody>
                            <CTableRow>
                              <CTableHeaderCell scope="row">{t("email")}</CTableHeaderCell>
                              <CTableDataCell>{requestData.currentEmail}</CTableDataCell>
                              <CTableDataCell className={requestData.currentEmail !== requestData.newEmail ? 'highlight-change' : ''}>
                                {requestData.newEmail}
                              </CTableDataCell>
                              <CTableDataCell>
                                {requestData.currentEmail !== requestData.newEmail ? (
                                  <span className="text-warning">●</span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </CTableDataCell>
                            </CTableRow>
                            <CTableRow>
                              <CTableHeaderCell scope="row">{t("specialization")}</CTableHeaderCell>
                              <CTableDataCell>{requestData.currentSpecialization}</CTableDataCell>
                              <CTableDataCell className={requestData.currentSpecialization !== requestData.newSpecialization ? 'highlight-change' : ''}>
                                {requestData.newSpecialization}
                              </CTableDataCell>
                              <CTableDataCell>
                                {requestData.currentSpecialization !== requestData.newSpecialization ? (
                                  <span className="text-warning">●</span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </CTableDataCell>
                            </CTableRow>
                            <CTableRow>
                              <CTableHeaderCell scope="row">{t("operationalUnit")}</CTableHeaderCell>
                              <CTableDataCell>{requestData.currentOperationalUnit || '-'}</CTableDataCell>
                              <CTableDataCell className={requestData.currentOperationalUnit !== requestData.newOperationalUnit ? 'highlight-change' : ''}>
                                {requestData.newOperationalUnit || '-'}
                              </CTableDataCell>
                              <CTableDataCell>
                                {requestData.currentOperationalUnit !== requestData.newOperationalUnit ? (
                                  <span className="text-warning">●</span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </CTableDataCell>
                            </CTableRow>
                          </CTableBody>
                        </CTable>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>

                {!isProcessed && (
                  <CRow className="mb-4">
                    <CCol md={12}>
                      <CCard className="decision-card">
                        <CCardBody>
                          <h3 className="mb-4">{t("review_decision")}</h3>
                          
                          <CForm>
                            <CRow className="mb-3">
                              <CCol md={12}>
                                <CFormLabel>{t("message_to_doctor")} ({t("optional")}):</CFormLabel>
                                <CFormTextarea
                                  rows={4}
                                  value={message}
                                  onChange={(e) => setMessage(e.target.value)}
                                  placeholder={t("add_message_placeholder")}
                                />
                              </CCol>
                            </CRow>

                            <CRow>
                              <CCol md={12} className="text-center">
                                <CButton
                                  color="success"
                                  size="lg"
                                  className="me-3"
                                  onClick={() => handleDecision('APPROVED')}
                                  disabled={processing}
                                >
                                  {processing && decision === 'APPROVED' ? (
                                    <>
                                      <CSpinner size="sm" /> {t("processing")}
                                    </>
                                  ) : (
                                    t("approve_request")
                                  )}
                                </CButton>
                                <CButton
                                  color="danger"
                                  size="lg"
                                  onClick={() => handleDecision('REJECTED')}
                                  disabled={processing}
                                >
                                  {processing && decision === 'REJECTED' ? (
                                    <>
                                      <CSpinner size="sm" /> {t("processing")}
                                    </>
                                  ) : (
                                    t("reject_request")
                                  )}
                                </CButton>
                              </CCol>
                            </CRow>
                          </CForm>
                        </CCardBody>
                      </CCard>
                    </CCol>
                  </CRow>
                )}
              </CContainer>
            </div>
          </div>
        </div>
      )}
      {!token_auth && <div className="login-container"></div>}
    </AuthGuard>
  );
};

export default ProfileChangeReview;